<template>
  <v-container fluid>
    <h5 class="grey--text section-label">{{ $t('surveys.title') }}</h5>
    <v-row>
      <v-col md="12">
        <h2 class="deep-purple--text lighten-3">{{ $t('surveys.subtitle') }}</h2>
      </v-col>
    </v-row>

    <survey-audience-tabs v-model="audience" class="mb-4" />

    <campaigns-table
      :campaigns="campaigns"
      :countries="countries"
      :loading="loadingCampaigns"
      :selected-id="selectedCampaignId"
      class="mb-4"
      @select="selectCampaign"
      @create="openCreate"
      @edit="openEdit"
      @remove="confirmRemove"
      @toggle="toggleCampaign"
    />

    <template v-if="selectedCampaign">
      <v-row v-if="countryFilterOptions.length" dense class="mb-2">
        <v-col cols="12" md="4">
          <v-select
            v-model="selectedCountry"
            :items="countryFilterOptions"
            item-text="text"
            item-value="value"
            :label="$t('surveys.results.filter_country')"
            clearable
            dense
            hide-details
          />
        </v-col>
      </v-row>

      <v-progress-linear v-if="loadingResults" indeterminate color="deep-purple" class="mb-4" />

      <v-alert v-else-if="resultsError" type="error" text class="mt-4">
        {{ $t('surveys.results.load_error') }}
      </v-alert>

      <v-alert v-else-if="!hasResults" type="info" text class="mt-4">
        {{ $t('surveys.results.no_data') }}
      </v-alert>

      <template v-else-if="isNps">
        <h3 class="text-subtitle-1 font-weight-bold mb-3">
          {{ $t('surveys.results.title', { name: selectedCampaign.name }) }}
        </h3>
        <nps-kpis
          :results="npsResults.global"
          :campaign-name="selectedCampaign.name"
          class="mb-4"
        />
        <v-row>
          <v-col cols="12" md="6">
            <nps-distribution
              :distribution="npsResults.global.distribution"
              :n="npsResults.global.n"
              :campaign-name="selectedCampaign.name"
            />
          </v-col>
          <v-col cols="12" md="6">
            <nps-breakdown-table
              :by-school="npsResults.bySchool"
              :by-country="npsResults.byCountry"
              :campaign-name="selectedCampaign.name"
            />
          </v-col>
        </v-row>

        <nps-comments
          v-if="npsResults.commentsCount > 0"
          :audience="audience"
          :campaign-id="selectedCampaignId"
          :campaign-name="selectedCampaign.name"
          :total="npsResults.commentsCount"
          :country="selectedCountry"
          class="mt-4"
        />
      </template>

      <template v-else>
        <h3 class="text-subtitle-1 font-weight-bold mb-3">
          {{ $t('surveys.results.title', { name: selectedCampaign.name }) }}
        </h3>
        <satisfaction-kpis
          :results="satisfactionResults"
          :campaign-name="selectedCampaign.name"
          class="mb-4"
        />
        <v-row>
          <v-col cols="12" md="6">
            <satisfaction-distribution
              :score-distribution="satisfactionResults.scoreDistribution"
              :campaign-name="selectedCampaign.name"
            />
          </v-col>
          <v-col cols="12" md="6">
            <satisfaction-breakdown-table
              :rows="breakdownRows"
              :campaign-name="selectedCampaign.name"
              @dimension-change="dimension = $event"
            />
          </v-col>
        </v-row>
      </template>
    </template>

    <campaign-form-dialog
      v-model="formOpen"
      :audience="audience"
      :campaign="editingCampaign"
      :countries="countries"
      @saved="loadCampaigns"
    />

    <v-dialog v-model="removeOpen" max-width="420">
      <v-card>
        <v-card-title class="text-h6">
          {{ $t('surveys.campaigns.delete.title') }}
        </v-card-title>
        <v-card-text>
          {{ $t('surveys.campaigns.delete.confirm', { name: (removingCampaign || {}).name }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="removeOpen = false">
            {{ $t('surveys.campaigns.delete.cancel') }}
          </v-btn>
          <v-btn color="red" dark :loading="removing" @click="removeCampaign">
            {{ $t('surveys.campaigns.delete.accept') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deactivateOpen" max-width="420">
      <v-card>
        <v-card-title class="text-h6">
          {{ $t('surveys.campaigns.deactivate.title') }}
        </v-card-title>
        <v-card-text>
          {{ $t('surveys.campaigns.deactivate.confirm', { name: (deactivatingCampaign || {}).name }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deactivateOpen = false">
            {{ $t('surveys.campaigns.deactivate.cancel') }}
          </v-btn>
          <v-btn color="deep-purple" dark :loading="toggling" @click="confirmDeactivate">
            {{ $t('surveys.campaigns.deactivate.accept') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import surveysService, { isNpsAudience } from '@/services/surveys.service';
import countryService from '@/services/country.service';
import { COUNTRIES_CUSTOMER_ID } from '@/constants/data';
import SurveyAudienceTabs from '@/components/surveys/SurveyAudienceTabs.vue';
import CampaignsTable from '@/components/surveys/CampaignsTable.vue';
import CampaignFormDialog from '@/components/surveys/CampaignFormDialog.vue';
import NpsKpis from '@/components/surveys/NpsKpis.vue';
import NpsDistribution from '@/components/surveys/NpsDistribution.vue';
import NpsBreakdownTable from '@/components/surveys/NpsBreakdownTable.vue';
import NpsComments from '@/components/surveys/NpsComments.vue';
import SatisfactionKpis from '@/components/studentSatisfaction/SatisfactionKpis.vue';
import SatisfactionDistribution
  from '@/components/studentSatisfaction/SatisfactionDistribution.vue';
import SatisfactionBreakdownTable
  from '@/components/studentSatisfaction/SatisfactionBreakdownTable.vue';
import snackbarMixin from '@/mixins/snackbar';

export default {
  name: 'Surveys',
  components: {
    SurveyAudienceTabs,
    CampaignsTable,
    CampaignFormDialog,
    NpsKpis,
    NpsDistribution,
    NpsBreakdownTable,
    NpsComments,
    SatisfactionKpis,
    SatisfactionDistribution,
    SatisfactionBreakdownTable,
  },
  mixins: [snackbarMixin],
  data: () => ({
    audience: 'STUDENTS',
    campaigns: [],
    countries: [],
    loadingCampaigns: false,
    selectedCampaignId: null,
    selectedCountry: null,
    countryFilterOptions: [],
    formOpen: false,
    editingCampaign: null,
    removeOpen: false,
    removingCampaign: null,
    removing: false,
    deactivateOpen: false,
    deactivatingCampaign: null,
    toggling: false,
    npsResults: null,
    satisfactionResults: null,
    dimension: 'grade',
    loadingResults: false,
    resultsError: false,
    // Token de generación: loadResults() puede dispararse de nuevo (cambio
    // de audiencia, de campaña o de país) antes de que una llamada anterior
    // resuelva. Sin esto, una respuesta vieja que llega tarde puede
    // pisar countryFilterOptions/npsResults/satisfactionResults con datos
    // de la audiencia o el filtro equivocado (KB-778, code review).
    resultsRequestId: 0,
  }),
  computed: {
    selectedCampaign() {
      return this.campaigns.find((c) => c.id === this.selectedCampaignId) || null;
    },
    isNps() {
      return isNpsAudience(this.audience);
    },
    // n = 0 (respuesta OK pero sin datos) muestra el estado vacío, no KPIs en cero.
    hasResults() {
      if (this.isNps) return Boolean(this.npsResults && this.npsResults.global.n > 0);
      return Boolean(this.satisfactionResults && this.satisfactionResults.n > 0);
    },
    breakdownRows() {
      if (!this.satisfactionResults) return [];
      const map = {
        grade: this.satisfactionResults.byGrade,
        school: this.satisfactionResults.bySchool,
        country: this.satisfactionResults.byCountry,
      };
      return map[this.dimension] || [];
    },
  },
  watch: {
    audience() {
      // Se limpian ambos resultados al cambiar de audiencia: si no, se ve por
      // un instante el renderer de la audiencia anterior mientras carga.
      this.npsResults = null;
      this.satisfactionResults = null;
      // Reset explícito, no implícito: `selectedCountry` mezcla dos tipos
      // según la audiencia (nombre de país para NPS, id numérico para
      // estudiantes — ver surveys.service.ts). Antes dependía únicamente de
      // que loadCampaigns() pusiera selectedCampaignId en null y ESO
      // disparara el watcher de abajo — encontrado en code review (KB-778)
      // como una cadena indirecta demasiado frágil para un campo con este
      // riesgo de choque de tipos entre audiencias.
      this.selectedCountry = null;
      // Idem: las opciones del filtro son de la audiencia anterior (un
      // nombre de país para NPS, un id numérico para estudiantes) — sin
      // este reset, quedan visibles y aplicables durante la ventana en que
      // se cargan los resultados de la audiencia nueva (KB-778, code review).
      this.countryFilterOptions = [];
      // El reset de `dimension` vive en loadResults(), no acá: el remount de
      // SatisfactionBreakdownTable no depende de este watcher sino de que
      // `loadingResults` pase por true/false, y eso también ocurre al elegir
      // otra campaña dentro de la misma audiencia (sin pasar por este watcher).
      this.loadCampaigns();
    },
    selectedCampaignId() {
      this.selectedCountry = null;
      // Mismo motivo que el reset del watcher de `audience`, en la ruta
      // hermana: el select de país vive FUERA de la cadena v-if de
      // loading/error/resultados, así que las opciones de la campaña anterior
      // siguen visibles y aplicables mientras carga la nueva. Si el usuario
      // elige una en esa ventana, el bloque `if (!selectedCountry)` de
      // loadResults() ya no las recalcula y quedan congeladas (las de la
      // campaña vieja) hasta que se limpie el filtro (KB-778, re-review).
      this.countryFilterOptions = [];
      this.loadResults();
    },
    selectedCountry() {
      this.loadResults();
    },
  },
  async mounted() {
    await Promise.all([this.loadCampaigns(), this.loadCountries()]);
  },
  methods: {
    async loadCampaigns() {
      this.loadingCampaigns = true;
      this.selectedCampaignId = null;
      try {
        this.campaigns = await surveysService.listCampaigns(this.audience);
        this.preselectCampaign();
      } catch (_error) {
        this.campaigns = [];
        this.snackbarError(this.$t('general.error_msg'));
      } finally {
        this.loadingCampaigns = false;
      }
    },
    async loadCountries() {
      try {
        const { data } = await countryService.findAll();
        this.countries = (data || []).filter((c) => COUNTRIES_CUSTOMER_ID.includes(c.id));
      } catch (_error) {
        this.countries = [];
      }
    },
    // La campaña activa es la que le interesa a quien entra; si no hay ninguna,
    // la más reciente (el backend devuelve la lista ordenada por id descendente).
    preselectCampaign() {
      const active = this.campaigns.find((c) => c.status === 'ACTIVE');
      const fallback = this.campaigns[0];
      this.selectedCampaignId = (active || fallback || {}).id ?? null;
    },
    selectCampaign(campaign) {
      this.selectedCampaignId = campaign.id;
    },
    openCreate() {
      this.editingCampaign = null;
      this.formOpen = true;
    },
    openEdit(campaign) {
      this.editingCampaign = campaign;
      this.formOpen = true;
    },
    confirmRemove(campaign) {
      this.removingCampaign = campaign;
      this.removeOpen = true;
    },
    async removeCampaign() {
      this.removing = true;
      try {
        await surveysService.deleteCampaign(this.audience, this.removingCampaign.id);
        this.snackbarSuccess(this.$t('surveys.campaigns.deleted'));
        this.removeOpen = false;
        await this.loadCampaigns();
      } catch (error) {
        // 409 = la campaña tiene respuestas. El botón ya está deshabilitado en
        // ese caso, pero la lista pudo quedar desactualizada.
        const message = error?.response?.status === 409
          ? this.$t('surveys.campaigns.delete.has_responses')
          : this.$t('general.error_msg');
        this.snackbarError(message);
      } finally {
        this.removing = false;
      }
    },
    // Desactivar pide confirmación (mismo patrón que confirmRemove/removeCampaign);
    // reactivar se aplica directo, sin diálogo previo.
    toggleCampaign(item) {
      if (item.active) {
        this.deactivatingCampaign = item;
        this.deactivateOpen = true;
        return;
      }
      this.applyToggle(item, true);
    },
    confirmDeactivate() {
      this.applyToggle(this.deactivatingCampaign, false);
    },
    async applyToggle(item, active) {
      this.toggling = true;
      try {
        await surveysService.toggleCampaignActive(this.audience, item.id, active);
        this.snackbarSuccess(
          this.$t(active ? 'surveys.campaigns.activated' : 'surveys.campaigns.deactivated'),
        );
        this.deactivateOpen = false;
        await this.loadCampaigns();
      } catch (error) {
        // 409 = la reactivación se solapa en fechas con otra campaña activa.
        const message = error?.response?.status === 409
          ? this.$t('surveys.campaigns.overlap')
          : this.$t('general.error_msg');
        this.snackbarError(message);
      } finally {
        this.toggling = false;
      }
    },
    async loadResults() {
      // SatisfactionBreakdownTable se destruye/recrea cada vez que
      // `loadingResults` pasa por true y vuelve a false (así lo arma el
      // v-if/v-else-if del template), y siempre nace con su dimension interna
      // en 'grade' (no emite en el mount). Ese remount ocurre acá tanto al
      // cambiar de audiencia como al elegir otra campaña dentro de la misma
      // audiencia, así que el reset va en este método (el único punto en
      // común) y no en el watcher de `audience`, que solo cubre uno de los
      // dos casos.
      this.dimension = 'grade';
      // Se incrementa siempre, incluso en este early-return: si había una
      // request en vuelo para la campaña anterior, esto la invalida para que
      // no pise el estado recién limpiado cuando resuelva.
      this.resultsRequestId += 1;
      if (!this.selectedCampaignId) {
        this.npsResults = null;
        this.satisfactionResults = null;
        // Sin esto el flag queda colgado en true: la request en vuelo que
        // acabamos de invalidar ya no lo va a limpiar (su `finally` compara
        // el token). Hoy no se ve, porque todo el bloque de resultados está
        // detrás de v-if="selectedCampaign" — pero el invariante no debería
        // depender de que el template lo tape (KB-778, re-review).
        this.loadingResults = false;
        return;
      }
      this.loadingResults = true;
      this.resultsError = false;
      // Capturado antes del await, no leído de nuevo después: `this.isNps`
      // depende de `audience`, que puede cambiar mientras esta request está
      // en vuelo (el usuario cambia de audiencia antes de que responda la
      // anterior). Sin esta captura, la rama NPS/satisfacción se decide con
      // la audiencia VIEJA pero el resultado se asigna a la propiedad de la
      // audiencia NUEVA, dejando la otra en null y disparando un error falso
      // en el bloque de countryFilterOptions (KB-778, code review).
      const { isNps, selectedCountry } = this;
      // Token de generación (incrementado arriba, antes del early-return):
      // si otro loadResults() arranca antes de que este resuelva (cambio de
      // audiencia/campaña/país en vuelo), una respuesta vieja que llega
      // tarde no debe pisar el estado de la request más reciente ni tocar
      // loadingResults/resultsError por ella (KB-778, code review — el mismo
      // patrón que ya usa NpsComments con lastFetchKey).
      const requestId = this.resultsRequestId;
      try {
        const results = isNps
          ? await surveysService.getNpsResults(
            this.audience,
            this.selectedCampaignId,
            { country: selectedCountry },
          )
          : await surveysService.getSatisfactionResults(
            this.selectedCampaignId,
            selectedCountry,
          );
        if (requestId !== this.resultsRequestId) return;
        if (isNps) {
          this.npsResults = results;
        } else {
          this.satisfactionResults = results;
        }
        // Las opciones del filtro se derivan del resultado SIN filtrar, para no
        // colapsar a un solo país después de aplicar uno.
        if (!selectedCountry) {
          this.countryFilterOptions = isNps
            ? (results.byCountry || [])
              .filter((c) => c.country)
              .map((c) => ({ text: c.country, value: c.country }))
            : (results.byCountry || [])
              .filter((c) => c.countryId)
              .map((c) => ({ text: c.label, value: c.countryId }));
        }
      } catch (_error) {
        if (requestId !== this.resultsRequestId) return;
        // El snackbar es transitorio; `resultsError` además muestra un alert
        // persistente, para no confundir una caída con "sin datos".
        this.resultsError = true;
        this.snackbarError(this.$t('general.error_msg'));
      } finally {
        if (requestId === this.resultsRequestId) {
          this.loadingResults = false;
        }
      }
    },
  },
};
</script>

<style scoped>
.section-label {
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
</style>
